import { z } from 'zod';

const DateStr = z.iso.date();

/**
 * Проверка контрагента (КА-07–КА-10): результат проверки по источнику, находки с важностью по правилам,
 * «стоп» закрывает выбор в закупке и договор до решения Директора с причиной.
 */
export type CheckSeverity = 'stop' | 'attention' | 'info';

/** Виды находок и важность по умолчанию (КА-10) */
export const CHECK_FINDINGS = {
  dishonest_registry: { title: 'Реестр недобросовестных участников', severity: 'stop' },
  fake_company: { title: 'Признаки лжепредприятия', severity: 'stop' },
  esf_restricted: { title: 'Ограничение на выписку ЭСФ', severity: 'stop' },
  sanctions: { title: 'Санкционные списки', severity: 'stop' },
  tax_debt: { title: 'Налоговая задолженность', severity: 'attention' },
  enforcement: { title: 'Исполнительные производства', severity: 'attention' },
  court_defendant: { title: 'Суды: ответчик', severity: 'attention' },
  recent_registration: { title: 'Недавняя регистрация', severity: 'attention' },
  leader_changed: { title: 'Смена руководителя или участников', severity: 'info' },
  other: { title: 'Другое', severity: 'info' },
} as const satisfies Record<string, { title: string; severity: CheckSeverity }>;
export type CheckFinding = keyof typeof CHECK_FINDINGS;

/** Важность находок по правилам компании (КА-10): то, что компания не меняла, берётся по умолчанию */
export const CheckSeveritySchema = z.enum(['stop', 'attention', 'info']);
export type CheckRules = Partial<Record<CheckFinding, CheckSeverity>>;
export const CheckRulesInput = z.object({
  severities: z.partialRecord(
    z.enum(Object.keys(CHECK_FINDINGS) as [CheckFinding, ...CheckFinding[]]),
    CheckSeveritySchema,
  ),
});
export type CheckRulesInput = z.infer<typeof CheckRulesInput>;
/** Ключ настройки компании */
export const CHECK_RULES_KEY = 'suppliers.check_rules';

/** Важность находки с учётом правил компании */
export function findingSeverity(kind: string, rules?: CheckRules | null): CheckSeverity {
  const base = (CHECK_FINDINGS as Record<string, { severity: CheckSeverity }>)[kind]?.severity ?? 'info';
  return (rules as Record<string, CheckSeverity> | null | undefined)?.[kind] ?? base;
}
/** Полная таблица «вид находки → важность» для экрана */
export function effectiveRules(rules?: CheckRules | null): Record<CheckFinding, CheckSeverity> {
  return Object.fromEntries(
    (Object.keys(CHECK_FINDINGS) as CheckFinding[]).map((k) => [k, findingSeverity(k, rules)]),
  ) as Record<CheckFinding, CheckSeverity>;
}

/**
 * Источники проверки (КА-08): подключаются как адаптеры. Сейчас работает только ручной ввод результата;
 * Kompra и КГД ждут ключей доступа (решение владельца), Adata и Statsnet — запасные.
 */
export const CHECK_SOURCES = {
  manual: { title: 'Ввод вручную', connected: true },
  kompra: { title: 'Kompra', connected: false },
  kgd: { title: 'КГД «Сведения по контрагентам»', connected: false },
  adata: { title: 'Adata (запасной)', connected: false },
  statsnet: { title: 'Statsnet (запасной)', connected: false },
} as const;
export type CheckSource = keyof typeof CHECK_SOURCES;

export const CheckInput = z.object({
  source: z.enum(Object.keys(CHECK_SOURCES) as [CheckSource, ...CheckSource[]]).default('manual'),
  checkedOn: DateStr.optional(),
  findings: z
    .array(
      z.object({
        kind: z.enum(Object.keys(CHECK_FINDINGS) as [CheckFinding, ...CheckFinding[]]),
        text: z.string().trim().max(500).nullable().optional(),
      }),
    )
    .max(30)
    .default([]),
  note: z.string().trim().max(1000).nullable().optional(),
});
export type CheckInput = z.infer<typeof CheckInput>;

export const CheckResolveInput = z.object({ reason: z.string().trim().min(3).max(2000) });
export type CheckResolveInput = z.infer<typeof CheckResolveInput>;

export const CounterpartyCheck = z.object({
  id: z.uuid(),
  source: z.string(),
  checkedOn: DateStr,
  result: z.enum(['ok', 'attention', 'stop']),
  findings: z.array(
    z.object({ kind: z.string(), title: z.string(), severity: z.string(), text: z.string().nullable() }),
  ),
  note: z.string().nullable(),
  byName: z.string().nullable(),
  /** Решение Директора по «стопу»: допустить, несмотря на результат */
  resolvedReason: z.string().nullable(),
  resolvedByName: z.string().nullable(),
  resolvedAt: z.string().nullable(),
});
export type CounterpartyCheck = z.infer<typeof CounterpartyCheck>;

/** Результат по набору находок: любой «стоп» — стоп, иначе любое «внимание» — внимание */
export function checkResult(kinds: CheckFinding[], rules?: CheckRules | null): 'ok' | 'attention' | 'stop' {
  const sev = kinds.map((k) => findingSeverity(k, rules));
  return sev.includes('stop') ? 'stop' : sev.includes('attention') ? 'attention' : 'ok';
}
